import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  IconButton,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { CalendarIcon, X as ClearIcon } from "lucide-react";

interface DateRangePickerProps {
  onClear: () => void;
  onApply: (starDate: string, endDate: string) => void;
}

type PresetPeriod =
  | "custom"
  | "currentMonth"
  | "previousMonth"
  | "currentYear"
  | "previousYear";

const DateRangePicker = ({ onClear, onApply }: DateRangePickerProps) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [selectedPeriod, setSelectedPeriod] =
    React.useState<PresetPeriod>("currentMonth");
  const open = Boolean(anchorEl);

  // Format date for display
  const formatDateForDisplay = (dateStr: string): string => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const getPresetDates = useCallback((
    period: PresetPeriod
  ): { start: string; end: string } => {
    const formatDateToStringLocal = (date: Date): string => {
      return date
        .toLocaleDateString("sv-SE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .split("/")
        .join("-");
    };
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let start: Date;
    let end: Date;

    switch (period) {
      case "currentMonth": {
        start = new Date(currentYear, currentMonth, 1);
        end = new Date(currentYear, currentMonth + 1, 0);
        break;
      }
      case "previousMonth": {
        start = new Date(currentYear, currentMonth - 1, 1);
        end = new Date(currentYear, currentMonth, 0);
        break;
      }
      case "currentYear": {
        start = new Date(currentYear, 0, 1);
        end = new Date(currentYear, 11, 31);
        break;
      }
      case "previousYear": {
        start = new Date(currentYear - 1, 0, 1);
        end = new Date(currentYear - 1, 11, 31);
        break;
      }
      default: {
        return { start: startDate, end: endDate };
      }
    }

    // Set correct times for start and end
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return {
      start: formatDateToStringLocal(start),
      end: formatDateToStringLocal(end),
    };
  }, []);

  // Set default dates when component mounts
  React.useEffect(() => {
    if (!startDate && !endDate) {
      const { start, end } = getPresetDates("currentMonth");
      onApply(start, end);
      setStartDate(start);
      setEndDate(end);
    }
  }, [getPresetDates, onApply, startDate, endDate]);

  const handlePresetChange = (period: PresetPeriod) => {
    setSelectedPeriod(period);
    if (period !== "custom") {
      const { start, end } = getPresetDates(period);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onApply(startDate, endDate);
    handleClose();
  };

  const handleClear = () => {
    onClear();
    setSelectedPeriod("custom");
    handleClose();
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return "Välj datumintervall";
    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
      endDate
    )}`;
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          p: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <CalendarIcon size={20} />
        <Typography variant="body2">{formatDateRange()}</Typography>
        {(startDate || endDate) && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <ClearIcon size={16} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Paper sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle1" gutterBottom>
            Välj datumintervall
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Period"
              onChange={(e) =>
                handlePresetChange(e.target.value as PresetPeriod)
              }
            >
              <MenuItem value="custom">Anpassad period</MenuItem>
              <MenuItem value="currentMonth">Denna månad</MenuItem>
              <MenuItem value="previousMonth">Föregående månad</MenuItem>
              <MenuItem value="currentYear">Detta år</MenuItem>
              <MenuItem value="previousYear">Föregående år</MenuItem>
            </Select>
          </FormControl>

          {selectedPeriod === "custom" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}
            >
              <TextField
                label="Startdatum"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <TextField
                label="Slutdatum"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                error={endDate < startDate}
                helperText={
                  endDate < startDate
                    ? "Slutdatum kan inte vara före startdatum"
                    : ""
                }
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleClear} size="small">
              Rensa
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="small"
              disabled={endDate < startDate}
            >
              Använd
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default DateRangePicker;
